<script setup>
/**
 * @description 简洁页码切换组件
 * @param {number} current - 当前页码
 * @param {number} total - 总页数
 * @emits change - 页码切换事件
 */

// 1. Imports
import { ref, nextTick, watch } from 'vue'
import { IconLeft, IconRight } from '@arco-design/web-vue/es/icon'

// 2. Props
const props = defineProps({
  current: {
    type: Number,
    required: true,
  },
  total: {
    type: Number,
    required: true,
  },
})

// 3. Emits
const emit = defineEmits(['update:current', 'change'])

// 4. Refs/Reactive State
const isEditing = ref(false)
const inputPage = ref(props.current)
const inputRef = ref(null)

// 5. Methods
const handlePrev = () => {
  if (props.current > 1) {
    const newPage = props.current - 1
    emit('update:current', newPage)
    emit('change', newPage)
  }
}

const handleNext = () => {
  if (props.current < props.total) {
    const newPage = props.current + 1
    emit('update:current', newPage)
    emit('change', newPage)
  }
}

const startEdit = () => {
  inputPage.value = props.current
  isEditing.value = true
  nextTick(() => {
    inputRef.value?.focus()
    inputRef.value?.select()
  })
}

const handleInputBlur = () => {
  submitPage()
}

const handleInputKeydown = (e) => {
  if (e.key === 'Enter') {
    submitPage()
  } else if (e.key === 'Escape') {
    isEditing.value = false
  }
}

const submitPage = () => {
  if (!isEditing.value) return

  let page = parseInt(inputPage.value)
  if (isNaN(page)) {
    page = props.current
  } else {
    page = Math.max(1, Math.min(page, props.total))
  }

  if (page !== props.current) {
    emit('update:current', page)
    emit('change', page)
  }
  isEditing.value = false
}

// 6. Watchers
watch(
  () => props.current,
  (newVal) => {
    inputPage.value = newVal
  }
)
</script>

<template>
  <div class="simple-pagination">
    <button class="nav-btn" :disabled="current <= 1" aria-label="Previous page" @click="handlePrev">
      <icon-left />
    </button>
    <div class="page-info">
      <div v-if="isEditing" class="current-input-wrapper">
        <input
          ref="inputRef"
          v-model="inputPage"
          type="text"
          class="current-input"
          @blur="handleInputBlur"
          @keydown="handleInputKeydown"
        />
      </div>
      <span v-else class="current" @click="startEdit">{{ current }}</span>
      <span class="separator">/</span>
      <span class="total">{{ total }}</span>
    </div>
    <button class="nav-btn" :disabled="current >= total" aria-label="Next page" @click="handleNext">
      <icon-right />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.simple-pagination {
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 8px;
    border: none;
    background: transparent;
    color: $text-subtitle-color;
    cursor: pointer;
    transition: all 0.3s;
    border-radius: 2px;

    &:hover:not(:disabled) {
      color: $primary-color;
    }

    &:disabled {
      color: #c9cdd4;
      cursor: not-allowed;
    }

    :deep(.arco-icon) {
      font-size: 12px;
    }
  }

  .page-info {
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 22px;

    .current {
      min-width: 40px;
      padding: 2px 8px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f2f3f5;
      border-radius: 6px;
      font-weight: 600;
      font-size: 14px;
      color: $text-content-color;
      cursor: pointer;
      transition: background-color 0.3s;

      &:hover {
        background-color: $border-hover-color;
      }
    }

    .current-input-wrapper {
      width: 40px;
      height: 28px;
    }

    .current-input {
      width: 100%;
      height: 100%;
      padding: 0 8px;
      border: 1px solid $primary-color;
      border-radius: 6px;
      background-color: $bg-white;
      color: $text-content-color;
      font-size: 14px;
      font-weight: 600;
      text-align: center;
      outline: none;
    }

    .separator {
      padding: 0 8px;
      color: $text-title-color;
      font-size: 14px;
    }

    .total {
      min-width: 24px;
      padding-right: 8px;
      text-align: center;
      color: $text-title-color;
      font-size: 14px;
    }
  }
}
</style>
